/**
 * Glow Ladies Society — Event / Workshop product page
 *
 * Custom element <event-product> drives:
 *  - ticket (variant) selection + quantity (= attendee count)
 *  - live capacity / sold-out state from real Shopify inventory
 *  - hero media gallery (thumbnail swap + lightbox), reusing native product images
 *  - the attendee-info step: collected BEFORE items are added to the cart (see README
 *    note below for why), then submitted to Shopify's native AJAX Cart API as one
 *    line item per ticket (quantity: 1 each) with unique `properties`, so multiple
 *    tickets of the same variant never merge into a single line and attendee data
 *    never gets mixed up between tickets. No app, no custom backend.
 *  - a small sticky mobile CTA mirrored to the main CTA's visibility/state.
 *
 * Design decision (documented per project instructions): accelerated/dynamic
 * checkout buttons (Shop Pay etc.) are intentionally NOT rendered anywhere on this
 * template, because those buttons submit directly to checkout and would bypass the
 * attendee-info validation below. Regular checkout is unaffected once items are in
 * the cart, because by construction every line already carries complete attendee
 * properties before it's added.
 */
(() => {
  'use strict';

  const ROUTES_ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_ADD_URL = `${ROUTES_ROOT.replace(/\/$/, '')}/cart/add.js`;

  class EventProductApp extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      const dataEl = this.querySelector('[data-event-json]');
      if (!dataEl) return;
      this.data = JSON.parse(dataEl.textContent);
      this.strings = this.data.strings || {};

      this.variantsById = new Map(this.data.variants.map((v) => [String(v.id), v]));
      this.selectedVariantId = String(
        this.data.selectedVariantId || this.data.variants[0]?.id || ''
      );
      this.quantity = 1;

      this._bindGallery();
      this._bindTicketPicker();
      this._bindQuantity();
      this._bindCta();
      this._bindStickyCta();
      this._bindAttendeeDialog();

      this._render();

      this._bindUrgencyToast();
      this._bindEmailPopup();
    }

    // ------------------------------------------------------------- urgency
    // Remaining count shown to shoppers: the configured urgency number, but
    // never more than the real inventory when inventory is tracked.
    _shownRemaining(available) {
      const urgency = this.data.urgency || {};
      if (!urgency.enabled || !urgency.count) return available;
      if (available === null) return urgency.count;
      return Math.min(urgency.count, available);
    }

    _bindUrgencyToast() {
      const urgency = this.data.urgency || {};
      this.toast = this.querySelector('[data-event-toast]');
      if (!this.toast || !urgency.enabled || !urgency.toastEnabled) return;

      const storageKey = `gls-event-toast-${this.dataset.productId}`;
      try {
        if (sessionStorage.getItem(storageKey)) return;
      } catch (e) {
        /* storage unavailable — still show once */
      }

      const hide = () => this.toast.setAttribute('data-visible', 'false');
      this.toast
        .querySelector('[data-event-toast-close]')
        ?.addEventListener('click', hide);

      window.setTimeout(() => {
        const variant = this.variantsById.get(this.selectedVariantId);
        const available = this._availableForVariant(variant);
        const soldOut = !variant || variant.available === false || available === 0;
        if (soldOut || this.dialog?.open || this.emailPopup?.open) return;

        const titleEl = this.toast.querySelector('[data-event-toast-title]');
        if (titleEl) {
          titleEl.textContent = (urgency.badgeText || 'Son {count} kontenjan kaldı!').replace(
            '{count}',
            this._shownRemaining(available)
          );
        }
        this.toast.setAttribute('data-visible', 'true');
        try {
          sessionStorage.setItem(storageKey, '1');
        } catch (e) {
          /* ignore */
        }
        window.setTimeout(hide, 9000);
      }, Math.max(1, urgency.toastDelay || 4) * 1000);
    }

    // ----------------------------------------------------------- email popup
    _bindEmailPopup() {
      const cfg = this.data.emailPopup || {};
      this.emailPopup = this.querySelector('[data-event-email-popup]');
      if (!this.emailPopup || !cfg.enabled) return;

      const storageKey = 'gls-email-popup';
      const readState = () => {
        try {
          return JSON.parse(localStorage.getItem(storageKey) || 'null');
        } catch (e) {
          return null;
        }
      };
      const writeState = (state) => {
        try {
          localStorage.setItem(storageKey, JSON.stringify({ state, at: Date.now() }));
        } catch (e) {
          /* ignore */
        }
      };

      const open = () => {
        if (this.emailPopup.open || this.dialog?.open) return;
        this.toast?.setAttribute('data-visible', 'false');
        if (typeof this.emailPopup.showModal === 'function') this.emailPopup.showModal();
      };
      const close = () => {
        this.emailPopup.close();
      };

      this.emailPopup
        .querySelector('[data-event-email-close]')
        ?.addEventListener('click', close);
      this.emailPopup.addEventListener('click', (e) => {
        if (e.target === this.emailPopup) close();
      });
      this.emailPopup.addEventListener('close', () => {
        const current = readState();
        if (!current || current.state !== 'subscribed') writeState('dismissed');
      });
      this.emailPopup.querySelector('form')?.addEventListener('submit', () => {
        writeState('subscribed');
      });

      // After a submit Shopify reloads the page with the form result — show it right away.
      if (this.emailPopup.querySelector('[data-event-email-open-now]')) {
        open();
        return;
      }

      const saved = readState();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (saved && (saved.state === 'subscribed' || Date.now() - saved.at < sevenDays)) return;

      let armed = true;
      const trigger = () => {
        if (!armed) return;
        armed = false;
        open();
      };
      window.setTimeout(trigger, Math.max(5, cfg.delay || 15) * 1000);
      // Desktop exit intent: pointer leaves through the top of the window.
      document.addEventListener('mouseout', (e) => {
        if (!e.relatedTarget && e.clientY <= 0) trigger();
      });
    }

    // ---------------------------------------------------------------- gallery
    _bindGallery() {
      this.mainMedia = this.querySelector('[data-event-media-main]');
      this.thumbButtons = Array.from(this.querySelectorAll('[data-event-thumb]'));
      this.zoomTrigger = this.querySelector('[data-event-zoom-trigger]');
      this.lightbox = this.querySelector('[data-event-lightbox]');
      this.lightboxImg = this.lightbox ? this.lightbox.querySelector('img') : null;
      this.lightboxClose = this.lightbox
        ? this.lightbox.querySelector('[data-event-lightbox-close]')
        : null;

      this.thumbButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const videoSrc = btn.dataset.videoSrc;
          const src = btn.dataset.fullSrc;
          const alt = btn.dataset.alt || '';
          if (this.mainMedia && videoSrc) {
            this.mainMedia.innerHTML = `<iframe src="${videoSrc}" title="${alt}" loading="lazy" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen style="width:100%;height:100%;border:0;"></iframe>`;
          } else if (this.mainMedia && src) {
            const existingFrame = this.mainMedia.querySelector('iframe');
            if (existingFrame) {
              this.mainMedia.innerHTML = `<img src="${src}" alt="${alt}" loading="eager">`;
              this._bindZoomAfterSwap();
            } else {
              this.mainMedia.querySelector('img')?.setAttribute('src', src);
              this.mainMedia.querySelector('img')?.setAttribute('alt', alt);
            }
          }
          this.thumbButtons.forEach((b) => b.setAttribute('aria-current', 'false'));
          btn.setAttribute('aria-current', 'true');
        });
      });

      if (this.zoomTrigger && this.lightbox) {
        this.zoomTrigger.addEventListener('click', () => {
          const activeImg = this.mainMedia?.querySelector('img');
          if (activeImg && this.lightboxImg) {
            this.lightboxImg.src = activeImg.src;
            this.lightboxImg.alt = activeImg.alt;
          }
          if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
        });
      }
      if (this.lightboxClose && this.lightbox) {
        this.lightboxClose.addEventListener('click', () => this.lightbox.close());
        this.lightbox.addEventListener('click', (e) => {
          if (e.target === this.lightbox) this.lightbox.close();
        });
      }

      // Story-gallery images (section further down the page) open the same
      // shared lightbox dialog.
      this.querySelectorAll('[data-event-thumb-gallery]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const src = btn.dataset.fullSrc;
          const alt = btn.querySelector('img')?.alt || '';
          if (src && this.lightboxImg && this.lightbox) {
            this.lightboxImg.src = src;
            this.lightboxImg.alt = alt;
            if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
          }
        });
      });
    }

    // Re-attach the zoom button's live image reference after the main media
    // area gets swapped back from a video iframe to an <img>.
    _bindZoomAfterSwap() {
      if (!this.zoomTrigger) return;
      this.zoomTrigger.onclick = () => {
        const activeImg = this.mainMedia?.querySelector('img');
        if (activeImg && this.lightboxImg) {
          this.lightboxImg.src = activeImg.src;
          this.lightboxImg.alt = activeImg.alt;
        }
        if (typeof this.lightbox?.showModal === 'function') this.lightbox.showModal();
      };
    }

    // ---------------------------------------------------------- ticket picker
    _bindTicketPicker() {
      this.ticketInputs = Array.from(
        this.querySelectorAll('[data-event-ticket-input]')
      );
      this.ticketInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.selectedVariantId = input.value;
          this.quantity = 1;
          this._render();
        });
      });
    }

    // -------------------------------------------------------------- quantity
    _bindQuantity() {
      this.qtyInput = this.querySelector('[data-event-qty-input]');
      this.qtyMinus = this.querySelector('[data-event-qty-minus]');
      this.qtyPlus = this.querySelector('[data-event-qty-plus]');

      const clampQty = (val) => {
        const variant = this.variantsById.get(this.selectedVariantId);
        const max = this._shownRemaining(this._availableForVariant(variant));
        const upper = max === null ? 10 : Math.max(1, Math.min(max, 10));
        return Math.min(Math.max(1, val), upper);
      };

      this.qtyMinus?.addEventListener('click', () => {
        this.quantity = clampQty(this.quantity - 1);
        this._render();
      });
      this.qtyPlus?.addEventListener('click', () => {
        this.quantity = clampQty(this.quantity + 1);
        this._render();
      });
      this.qtyInput?.addEventListener('change', () => {
        this.quantity = clampQty(parseInt(this.qtyInput.value, 10) || 1);
        this._render();
      });
    }

    _availableForVariant(variant) {
      if (!variant) return 0;
      if (variant.inventoryManagement !== 'shopify') return null; // untracked = unlimited
      if (variant.inventoryPolicy === 'continue') return null;
      return typeof variant.inventoryQuantity === 'number'
        ? Math.max(0, variant.inventoryQuantity)
        : null;
    }

    // -------------------------------------------------------------------- CTA
    // Note: the CTA button appears twice in the markup (hero + closing CTA at
    // the bottom of the page) and both must open the same attendee dialog
    // using the currently selected ticket/quantity from the hero controls.
    _bindCta() {
      this.ctaButtons = Array.from(this.querySelectorAll('[data-event-cta-button]'));
      this.ctaButtons.forEach((btn) =>
        btn.addEventListener('click', () => this._openAttendeeDialog())
      );
    }

    // ------------------------------------------------------------- sticky CTA
    _bindStickyCta() {
      this.stickyBar = document.querySelector(
        `[data-event-sticky-cta="${this.sectionId}"]`
      );
      this.stickyButton = this.stickyBar?.querySelector('[data-event-sticky-button]');
      this.stickyButton?.addEventListener('click', () => this._openAttendeeDialog());

      const ctaAnchor = this.querySelector('[data-event-cta-anchor]');
      const footer = document.querySelector('.gls-footer, footer');
      if (!ctaAnchor || !this.stickyBar || !('IntersectionObserver' in window)) return;

      let ctaVisible = false;
      let footerVisible = false;

      const update = () => {
        const show = !ctaVisible && !footerVisible;
        this.stickyBar.setAttribute('data-visible', show ? 'true' : 'false');
      };

      new IntersectionObserver(([entry]) => {
        ctaVisible = entry.isIntersecting;
        update();
      }).observe(ctaAnchor);

      if (footer) {
        new IntersectionObserver(([entry]) => {
          footerVisible = entry.isIntersecting;
          update();
        }).observe(footer);
      }
    }

    // ---------------------------------------------------------------- render
    _render() {
      const variant = this.variantsById.get(this.selectedVariantId);
      if (!variant) return;
      const available = this._availableForVariant(variant);
      const threshold = this.data.lowStockThreshold || 6;
      const soldOut = variant.available === false || available === 0;
      const urgency = this.data.urgency || {};
      const shown = this._shownRemaining(available);

      // urgency badge in the buy box
      const urgencyEl = this.querySelector('[data-event-urgency]');
      if (urgencyEl) {
        const textEl = urgencyEl.querySelector('[data-event-urgency-text]');
        if (urgency.enabled && !soldOut && shown !== null) {
          if (textEl) {
            textEl.textContent = (urgency.badgeText || 'Son {count} kontenjan kaldı!').replace(
              '{count}',
              shown
            );
          }
          urgencyEl.hidden = false;
        } else {
          urgencyEl.hidden = true;
        }
      }

      // price (may appear in both the hero and the closing CTA)
      this.querySelectorAll('[data-event-price-current]').forEach((el) => {
        el.textContent = this._formatMoney(variant.price);
      });
      this.querySelectorAll('[data-event-price-compare]').forEach((el) => {
        if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
          el.textContent = this._formatMoney(variant.compareAtPrice);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });
      // Plain-text remaining-capacity restatement (hero + closing CTA), shown
      // whenever inventory is tracked — this is a factual count, not an
      // urgency message, so it isn't gated by the low-stock threshold.
      this.querySelectorAll('[data-event-remaining-text]').forEach((el) => {
        if (shown === null) {
          el.hidden = true;
        } else {
          const template = this.strings.remainingCount || '{count} kişilik yer kaldı';
          el.textContent = soldOut
            ? this.strings.soldOut || 'Kontenjan Doldu'
            : template.replace('{count}', shown);
          el.hidden = false;
        }
      });

      // capacity
      const capacityWrap = this.querySelector('[data-event-capacity]');
      if (capacityWrap) {
        if (urgency.enabled) {
          // The urgency badge already carries this message — avoid saying it twice.
          capacityWrap.hidden = true;
        } else if (available === null) {
          capacityWrap.hidden = true;
        } else if (soldOut) {
          capacityWrap.hidden = true;
        } else if (available <= threshold) {
          capacityWrap.hidden = false;
          const msgEl = capacityWrap.querySelector('[data-event-capacity-message]');
          const barFill = capacityWrap.querySelector('[data-event-capacity-fill]');
          const template =
            this.strings.lowStockMessage || 'Son {count} kişilik yer!';
          if (msgEl) {
            msgEl.textContent = template.replace('{count}', available);
            msgEl.setAttribute('data-urgency', 'low');
          }
          if (barFill && variant.totalCapacity) {
            const pct = Math.max(
              4,
              Math.min(100, (available / variant.totalCapacity) * 100)
            );
            barFill.style.width = `${pct}%`;
          }
        } else {
          capacityWrap.hidden = true;
        }
      }

      // ticket option sold-out styling
      this.ticketInputs.forEach((input) => {
        const v = this.variantsById.get(input.value);
        const optAvailable = this._availableForVariant(v);
        const optSoldOut = v && (v.available === false || optAvailable === 0);
        const option = input.closest('[data-event-ticket-option]');
        if (option) option.setAttribute('data-sold-out', optSoldOut ? 'true' : 'false');
        input.disabled = !!optSoldOut;
      });

      // quantity bounds
      if (this.qtyInput) {
        this.qtyInput.value = String(this.quantity);
        const max = shown === null ? 10 : Math.max(1, Math.min(shown, 10));
        this.qtyInput.max = String(max);
      }
      if (this.qtyMinus) this.qtyMinus.disabled = this.quantity <= 1 || soldOut;
      if (this.qtyPlus) {
        const max = shown === null ? 10 : Math.min(shown, 10);
        this.qtyPlus.disabled = soldOut || this.quantity >= max;
      }

      // CTA state (hero + closing CTA)
      const qtyGroup = this.querySelector('[data-event-quantity-group]');
      if (qtyGroup) qtyGroup.hidden = soldOut;
      this.ctaButtons.forEach((btn) => {
        btn.hidden = soldOut;
      });
      this.querySelectorAll('[data-event-sold-out-message]').forEach((el) => {
        el.hidden = !soldOut;
      });
      const waitlist = this.querySelector('[data-event-waitlist]');
      if (waitlist) waitlist.hidden = !soldOut;

      // sticky CTA content
      if (this.stickyBar) {
        const priceNode = this.stickyBar.querySelector('[data-event-sticky-price]');
        const capNode = this.stickyBar.querySelector('[data-event-sticky-capacity]');
        if (priceNode) priceNode.textContent = this._formatMoney(variant.price);
        if (capNode) {
          capNode.textContent =
            shown !== null && (urgency.enabled || shown <= threshold) && !soldOut
              ? (this.strings.stickyLowStock || '{count} yer kaldı').replace(
                  '{count}',
                  shown
                )
              : '';
        }
        if (this.stickyButton) this.stickyButton.hidden = soldOut;
        const stickySoldOut = this.stickyBar.querySelector('[data-event-sticky-sold-out]');
        if (stickySoldOut) stickySoldOut.hidden = !soldOut;
      }
    }

    _formatMoney(cents) {
      if (typeof window.Shopify?.formatMoney === 'function' && this.data.moneyFormat) {
        return window.Shopify.formatMoney(cents, this.data.moneyFormat);
      }
      // Fallback: simple decimal formatting in the shop's currency.
      const amount = (cents / 100).toFixed(2);
      return `${amount} ${this.data.currency || ''}`.trim();
    }

    // --------------------------------------------------------- attendee step
    _bindAttendeeDialog() {
      this.dialog = this.querySelector('[data-event-attendee-dialog]');
      this.dialogForm = this.dialog?.querySelector('form');
      this.dialogBlocksWrap = this.dialog?.querySelector('[data-event-attendee-blocks]');
      this.dialogTemplate = this.querySelector('[data-event-attendee-template]');
      this.dialogError = this.dialog?.querySelector('[data-event-form-error]');
      this.dialogCancel = this.dialog?.querySelector('[data-event-attendee-cancel]');

      this.dialogCancel?.addEventListener('click', () => this.dialog?.close());
      this.dialogForm?.addEventListener('submit', (e) => this._handleAttendeeSubmit(e));
    }

    _openAttendeeDialog() {
      if (!this.dialog || !this.dialogTemplate || !this.dialogBlocksWrap) return;
      this.dialogBlocksWrap.innerHTML = '';
      for (let i = 1; i <= this.quantity; i += 1) {
        const fragment = this.dialogTemplate.content.cloneNode(true);
        const block = fragment.querySelector('[data-attendee-block]');
        block.setAttribute('data-attendee-index', String(i));
        const heading = block.querySelector('[data-attendee-heading]');
        if (heading) {
          const label = this.strings.attendeeLabel || 'Katılımcı {n}';
          heading.textContent = label.replace('{n}', i);
        }
        block.querySelectorAll('[name]').forEach((field) => {
          field.name = `${field.name}__${i}`;
          field.id = `${field.id}__${i}`;
        });
        block.querySelectorAll('label[for]').forEach((label) => {
          label.setAttribute('for', `${label.getAttribute('for')}__${i}`);
        });
        this.dialogBlocksWrap.appendChild(fragment);
      }
      if (this.dialogError) this.dialogError.textContent = '';
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      this.dialogBlocksWrap.querySelector('input')?.focus();
    }

    _handleAttendeeSubmit(event) {
      event.preventDefault();
      const blocks = Array.from(
        this.dialogBlocksWrap.querySelectorAll('[data-attendee-block]')
      );
      let firstInvalid = null;
      const attendees = [];

      blocks.forEach((block) => {
        const idx = block.getAttribute('data-attendee-index');
        const getVal = (key) => {
          const el = block.querySelector(`[data-field="${key}"]`);
          return el ? el.value.trim() : '';
        };
        const requiredFields = [
          ['first_name', this.strings.errorRequired || 'Bu alan zorunludur.'],
          ['last_name', this.strings.errorRequired || 'Bu alan zorunludur.'],
          ['phone', this.strings.errorRequired || 'Bu alan zorunludur.'],
          ['email', this.strings.errorRequired || 'Bu alan zorunludur.'],
        ];

        requiredFields.forEach(([key, msg]) => {
          const wrapper = block.querySelector(`[data-field-wrap="${key}"]`);
          const el = block.querySelector(`[data-field="${key}"]`);
          if (!wrapper || !el) return;
          let valid = el.value.trim().length > 0;
          if (valid && key === 'email') {
            valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(el.value.trim());
            if (!valid) msg && (wrapper.querySelector('[data-field-error]').textContent =
              this.strings.errorEmail || 'Geçerli bir e-posta adresi girin.');
          } else if (msg) {
            wrapper.querySelector('[data-field-error]').textContent = msg;
          }
          wrapper.setAttribute('data-invalid', valid ? 'false' : 'true');
          el.setAttribute('aria-invalid', valid ? 'false' : 'true');
          if (!valid && !firstInvalid) firstInvalid = el;
        });

        const consent = block.querySelector('[data-field="consent"]');
        const consentWrap = block.querySelector('[data-field-wrap="consent"]');
        if (consent && consentWrap) {
          const valid = consent.checked;
          consentWrap.setAttribute('data-invalid', valid ? 'false' : 'true');
          if (!valid && !firstInvalid) firstInvalid = consent;
        }

        attendees.push({
          index: idx,
          firstName: getVal('first_name'),
          lastName: getVal('last_name'),
          phone: getVal('phone'),
          email: getVal('email'),
          dietary: getVal('dietary'),
          note: getVal('note'),
        });
      });

      if (firstInvalid) {
        if (this.dialogError) {
          this.dialogError.textContent =
            this.strings.errorSummary || 'Lütfen işaretli alanları kontrol edin.';
        }
        firstInvalid.focus();
        return;
      }

      this._submitToCart(attendees);
    }

    async _submitToCart(attendees) {
      const submitBtn = this.dialog.querySelector('[data-event-attendee-submit]');
      if (submitBtn) submitBtn.disabled = true;
      if (this.dialogError) this.dialogError.textContent = '';

      const variant = this.variantsById.get(this.selectedVariantId);
      const eventLabel = [this.data.productTitle, this.data.eventDateLabel]
        .filter(Boolean)
        .join(' — ');

      const items = attendees.map((a) => ({
        id: Number(this.selectedVariantId),
        quantity: 1,
        properties: {
          [this.strings.propAttendeeName || 'Katılımcı Adı Soyadı']: `${a.firstName} ${a.lastName}`.trim(),
          [this.strings.propPhone || 'Telefon']: a.phone,
          [this.strings.propEmail || 'E-posta']: a.email,
          ...(a.dietary
            ? { [this.strings.propDietary || 'Alerji / Beslenme Notu']: a.dietary }
            : {}),
          ...(a.note ? { [this.strings.propNote || 'Not']: a.note } : {}),
          _event_ticket_index: a.index,
          _event_title: eventLabel,
        },
      }));

      try {
        const response = await fetch(CART_ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result?.description || result?.message || 'cart_add_failed');
        }
        document.dispatchEvent(
          new CustomEvent('event-product:added', { detail: { items: result } })
        );
        window.location.assign(`${ROUTES_ROOT.replace(/\/$/, '')}/cart`);
      } catch (err) {
        if (this.dialogError) {
          this.dialogError.textContent =
            this.strings.errorCartAdd ||
            'Sepete eklerken bir sorun oluştu, lütfen tekrar deneyin.';
        }
        if (submitBtn) submitBtn.disabled = false;
      }
    }
  }

  if (!customElements.get('event-product')) {
    customElements.define('event-product', EventProductApp);
  }
})();
